import type { FormEvent, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'

import Link from 'next/link'
import { useState } from 'react'
import { ArrowRight, ChevronDown, LoaderCircle, Sparkles } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { AuthLayout } from '@/components/auth/auth-layout'
import { Button, buttonClass } from '@/components/ui/button'
import { Field, FormMessage, inputClass, selectClass } from '@/components/ui/field'
import { getSessionUser } from '@/lib/auth/session'
import { safeNext } from '@/lib/routes'
import { INVESTOR_STYLES } from '@/lib/labels'

interface SignupProps {
    next: string
}

export const getServerSideProps: GetServerSideProps<SignupProps> = async (context) => {
    const next = safeNext(context.query.next)
    const user = await getSessionUser(context.req).catch(() => null)

    if (user && !user.isDemo) return { redirect: { destination: next, permanent: false } }

    return { props: { next } }
}

type Errors = Partial<Record<'name' | 'email' | 'password', string>>

const SignupPage = ({ next }: SignupProps): ReactNode => {
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [style, setStyle] = useState('trung-han')
    const [errors, setErrors] = useState<Errors>({})
    const [message, setMessage] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)

    const validate = (): Errors => {
        const found: Errors = {}

        if (name.trim().length < 2) found.name = 'Vui lòng nhập họ tên.'
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) found.email = 'Email chưa đúng định dạng.'
        if (password.length < 8) found.password = 'Mật khẩu cần tối thiểu 8 ký tự.'

        return found
    }

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setMessage(null)

        const found = validate()

        setErrors(found)

        if (Object.keys(found).length > 0) return

        setLoading(true)

        try {
            const response = await fetch('/api/auth/signup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, email, password, investorStyle: style })
            })
            const data = await response.json().catch(() => ({})) as { message?: string }

            if (!response.ok) {
                setMessage(data.message ?? 'Không tạo được tài khoản, vui lòng thử lại.')
                setLoading(false)
                return
            }

            window.location.assign(next)
        }
        catch {
            setMessage('Mất kết nối, vui lòng thử lại.')
            setLoading(false)
        }
    }

    return (
        <AuthLayout
            eyebrow='Tạo tài khoản'
            title={<>Bắt đầu miễn phí, <em>không cần thẻ.</em></>}
            description='Gói Free gồm bàn phân tích đầy đủ, bản đồ sóng ngành, radar mua sớm và watchlist tối đa 12 mã.'
        >
            <Seo title='Tạo tài khoản' description='Tạo tài khoản Horse Moon miễn phí để dùng bàn phân tích đa vai trò, radar mua sớm và bản đồ sóng ngành.' />

            <form onSubmit={submit} className='flex flex-col gap-5' noValidate>
                {message && <FormMessage tone='error'>{message}</FormMessage>}

                <Field label='Họ và tên' htmlFor='name' error={errors.name}>
                    <input id='name' autoComplete='name' value={name} onChange={(event) => setName(event.target.value)} className={inputClass} placeholder='Nguyễn Minh Anh' aria-invalid={Boolean(errors.name)} />
                </Field>

                <Field label='Email' htmlFor='email' error={errors.email}>
                    <input id='email' type='email' autoComplete='email' value={email} onChange={(event) => setEmail(event.target.value)} className={inputClass} placeholder='ban@congty.vn' aria-invalid={Boolean(errors.email)} />
                </Field>

                <Field label='Mật khẩu' htmlFor='password' error={errors.password} hint='Tối thiểu 8 ký tự.'>
                    <input id='password' type='password' autoComplete='new-password' value={password} onChange={(event) => setPassword(event.target.value)} className={inputClass} aria-invalid={Boolean(errors.password)} />
                </Field>

                <Field label='Phong cách đầu tư' htmlFor='style'>
                    <div className='relative'>
                        <select id='style' value={style} onChange={(event) => setStyle(event.target.value)} className={selectClass}>
                            {Object.entries(INVESTOR_STYLES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                        <ChevronDown className='pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                    </div>
                </Field>

                <Button type='submit' size='lg' disabled={loading} className='w-full'>
                    {loading ? <LoaderCircle className='size-4 animate-spin' aria-hidden='true' /> : null}
                    {loading ? 'Đang tạo tài khoản' : 'Tạo tài khoản'}
                    {!loading && <ArrowRight className='size-4' aria-hidden='true' />}
                </Button>

                <p className='text-xs leading-relaxed text-subtle'>
                    Phân tích trên Horse Moon chỉ mang tính tham khảo, không phải khuyến nghị mua bán. Bạn tự chịu trách nhiệm với quyết định đầu tư của mình.
                </p>
            </form>

            <a href={`/api/auth/demo?next=${encodeURIComponent(next)}`} className={buttonClass('outline', 'lg', 'mt-6 w-full')}>
                <Sparkles className='size-4 text-lime' aria-hidden='true' />
                Xem trước bằng tài khoản demo
            </a>

            <p className='mt-8 text-center text-sm text-muted-foreground'>
                Đã có tài khoản?{' '}
                <Link href={`/dang-nhap${next !== '/dashboard' ? `?next=${encodeURIComponent(next)}` : ''}`} className='text-lime underline underline-offset-4 hover:text-foreground'>Đăng nhập</Link>
            </p>
        </AuthLayout>
    )
}

export default SignupPage
